import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { InvoiceOrderStatus } from "@/core/api/graphql/types";
import type { SapOrdersFilterData, SapOrdersFilterChipsItem } from "@/shared/account";
import type { Ref } from "vue";
import { toEndDateFilterValue, toStartDateFilterValue } from "@/core/utilities";

const filterData: Ref<SapOrdersFilterData> = ref({ status: InvoiceOrderStatus.Unknown });
const appliedFilterData: Ref<SapOrdersFilterData> = ref({ ...filterData.value });

export function useSapOrdersFilter() {
  const { t, d } = useI18n();

  const isFilterEmpty = computed(() => {
    const { status, startDate, endDate } = appliedFilterData.value;

    return !startDate && !endDate && status == InvoiceOrderStatus.Unknown;
  });

  const isFilterDirty = computed(() => {
    return JSON.stringify(filterData.value) !== JSON.stringify(appliedFilterData.value);
  });

  const filterChipsItems = computed(() => {
    const items: SapOrdersFilterChipsItem[] = [];

    if (appliedFilterData.value.status?.length && appliedFilterData.value?.status != InvoiceOrderStatus.Unknown) {
      items.push({
        fieldName: "status",
        value: appliedFilterData.value.status,
        label: t("custom.orders.labels." + status.toLowerCase().split(" ").join("_")),
      });
    }

    const startDateFilterValue = toStartDateFilterValue(appliedFilterData.value.startDate);
    const endDateFilterValue = toEndDateFilterValue(appliedFilterData.value.endDate);

    if (startDateFilterValue) {
      const formattedDate = d(startDateFilterValue);
      items.push({
        fieldName: "startDate",
        value: appliedFilterData.value.startDate,
        label: t("common.labels.starts_from", [formattedDate]),
      });
    }
    if (endDateFilterValue) {
      const formattedDate = d(endDateFilterValue);
      items.push({
        fieldName: "endDate",
        value: appliedFilterData.value.endDate,
        label: t("common.labels.ends_to", [formattedDate]),
      });
    }
    return items;
  });

  function applyFilters() {
    if (JSON.stringify(appliedFilterData.value) === JSON.stringify(filterData.value)) {
      return;
    }
    appliedFilterData.value = { ...filterData.value };
  }

  function resetFilters() {
    filterData.value = { status: InvoiceOrderStatus.Unknown };
    appliedFilterData.value = { ...filterData.value };
  }

  function resetDataToApplied() {
    filterData.value = { ...appliedFilterData.value };
  }

  function removeFilterChipsItem(item: SapOrdersFilterChipsItem) {
    if (item.fieldName === "status") {
      appliedFilterData.value.status = InvoiceOrderStatus.Unknown;
    }

    if (item.fieldName === "startDate") {
      appliedFilterData.value.startDate = undefined;
    }

    if (item.fieldName === "endDate") {
      appliedFilterData.value.endDate = undefined;
    }

    filterData.value = { ...appliedFilterData.value };
  }

  return {
    filterData,
    appliedFilterData: computed(() => appliedFilterData.value),
    isFilterEmpty,
    isFilterDirty,
    filterChipsItems,
    applyFilters,
    resetFilters,
    resetDataToApplied,
    removeFilterChipsItem,
  };
}
